import { isEmpty } from "lodash";
import uniqid from "uniqid";
import { cloneDeep } from "lodash";
import { getAllowedCategories } from "utils/enclosure";

const getAtComponentPath = (root, dottedPath) =>
    dottedPath.split(".").reduce((acc, key) => acc?.[key], root);

export const getComponentTypeFromDraggableId = (draggableId = "") => {
    const parts = draggableId.split(".");
    if (parts.includes("subcomponents")) {
        return parts[parts.length - 2];
    }
    return parts[0];
};

export const isSubcomponentDraggableId = (draggableId = "") => draggableId.includes(".subcomponents.");

export const getParentDraggableId = (draggableId = "") => {
    const idx = draggableId.indexOf(".subcomponents.");
    return idx === -1 ? null : draggableId.slice(0, idx);
};

/**
 * Whether a draggable / droppable id belongs to an interior or exterior root component.
 * Ids are rooted at `<type>.<id>` (e.g. `wall.wall-1.subcomponents.door.door-2`).
 * @returns {"interior"|"exterior"}
 */
export const getDraggablePosition = (draggableId = "", intComponents = {}) => {
    const [type, id] = draggableId.split(".");
    return intComponents?.[type]?.[id] ? "interior" : "exterior";
};

export const getCompatibleSelectedIds = (activeDraggableId, selectedIds = [], intComponents) => {
    const activeIsSub = isSubcomponentDraggableId(activeDraggableId);
    const selected = selectedIds.includes(activeDraggableId)
        ? selectedIds
        : [activeDraggableId, ...selectedIds];

    const sameKind = selected.filter((id) => isSubcomponentDraggableId(id) === activeIsSub);
    if (!activeIsSub || !intComponents) return sameKind;

    const activePosition = getDraggablePosition(activeDraggableId, intComponents);
    return sameKind.filter((id) => getDraggablePosition(id, intComponents) === activePosition);
};

/** Recursively count nested subcomponents (and their children). */
const countNestedComponents = (componentData) => {
    const subcomponents = componentData?.subcomponents;
    if (!subcomponents || typeof subcomponents !== "object") return 0;

    return Object.values(subcomponents).reduce((sum, group) => {
        if (!group || typeof group !== "object") return sum;

        return (
            sum +
            Object.values(group).reduce((inner, child) => {
                if (child == null || isEmpty(child)) return inner;
                return inner + 1 + countNestedComponents(child);
            }, 0)
        );
    }, 0);
};

/** Total components in a drag: each selected item plus all nested sub / sub-sub components. */
export const countDraggedComponents = (draggableIds = [], components = {}) =>
    draggableIds.reduce((total, id) => {
        const data = getAtComponentPath(components, id);
        return total + 1 + countNestedComponents(data);
    }, 0);

export const intersectAllowedCategories = (draggableIds = []) => {
    if (!draggableIds.length) return [];

    return draggableIds.reduce((acc, id, index) => {
        const allowed = getAllowedCategories(getComponentTypeFromDraggableId(id)) || [];
        if (index === 0) return [...allowed];
        return acc.filter((category) => allowed.includes(category));
    }, []);
};

const sortDraggableIdsByDisplayOrder = (draggableIds, components) =>
    [...draggableIds].sort((a, b) => {
        const aOrder = getAtComponentPath(components, a)?.displayOrder ?? 0;
        const bOrder = getAtComponentPath(components, b)?.displayOrder ?? 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.localeCompare(b);
    });

const filterAllowedAtDestination = (draggableIds, destination, typeOfDrop) => {
    if (!destination?.droppableId) return draggableIds;

    const isSubComponent = typeOfDrop === "component" || typeOfDrop === "subcomponent";
    const destTarget = isSubComponent
        ? destination.droppableId.split(".")[0]
        : destination.droppableId.split(".")[0];

    return draggableIds.filter((id) => {
        const type = getComponentTypeFromDraggableId(id);
        return (getAllowedCategories(type) || []).includes(destTarget);
    });
};

const updateNestedSubcomponentCategories = (change, draggableId, componentData, destStoreyId) => {
    const subComponents = componentData?.subcomponents || {};
    if (isEmpty(subComponents)) return;

    const updatedSubComponents = Object.entries(subComponents).reduce((acc, [compType, compList]) => {
        const newCompList = Object.entries(compList || {}).reduce(
            (innerAcc, [compId, { subcomponents, ...compData }]) => {
                const extraProps = {};

                if (subcomponents) {
                    extraProps.subcomponents = Object.fromEntries(
                        Object.entries(subcomponents).reduce((subAcc, [subCompType, subCompList]) => {
                            const newSubCompList = Object.entries(subCompList || {}).reduce(
                                (listAcc, [subCompId, subCompData]) => [
                                    ...listAcc,
                                    [subCompId, { ...subCompData, category: destStoreyId }],
                                ],
                                []
                            );

                            return [...subAcc, [subCompType, Object.fromEntries(newSubCompList)]];
                        }, [])
                    );
                }

                return [
                    ...innerAcc,
                    [
                        compId,
                        {
                            ...compData,
                            category: destStoreyId,
                            ...extraProps,
                        },
                    ],
                ];
            },
            []
        );

        return [...acc, [compType, Object.fromEntries(newCompList)]];
    }, []);

    change(`model.modelData.components.${draggableId}.subcomponents`, Object.fromEntries(updatedSubComponents));
};

const multiMoveRoots = (change, destination, source, components, toMoveIds, { isRoomView = false, roomByRoom = false } = {}) => {
    const destCategory = destination.droppableId.split(".")[0];
    const sourceCategory = source.droppableId.split(".")[0];
    const destRoomId = isRoomView ? destination.droppableId.split(".")[1] : undefined;
    const sourceRoomId = isRoomView ? source.droppableId.split(".")[1] : undefined;

    const movingIds = new Set(toMoveIds.map((id) => id.split(".")[1]));

    const matchesDest = ({ category, roomId }) => {
        if (category !== destCategory) return false;
        if (!isRoomView) return true;
        return roomId === destRoomId || (!roomId && destRoomId === `room-default-${destCategory}`);
    };

    const matchesSource = ({ category, roomId }) => {
        if (category !== sourceCategory) return false;
        if (!isRoomView) return true;
        return roomId === sourceRoomId || (!roomId && sourceRoomId === `room-default-${sourceCategory}`);
    };

    const allMapped = mapComponents(components).filter((el) => !isEmpty(el));

    const destFull = allMapped.filter(matchesDest);
    const withoutMoving = destFull.filter(({ componentId }) => !movingIds.has(componentId));

    const movingBeforeDest = destFull
        .slice(0, destination.index)
        .filter(({ componentId }) => movingIds.has(componentId)).length;

    let insertAt = destination.index - movingBeforeDest;
    insertAt = Math.max(0, Math.min(insertAt, withoutMoving.length));

    const movedItems = toMoveIds.map((id) => {
        const [componentType, componentId] = id.split(".");
        return { componentType, componentId, draggableId: id };
    });

    const newDestList = [...withoutMoving.slice(0, insertAt), ...movedItems, ...withoutMoving.slice(insertAt)];

    newDestList.forEach((item, index) => {
        const basePath = `model.modelData.components.${item.componentType}.${item.componentId}`;
        change(`${basePath}.displayOrder`, index);

        if (movingIds.has(item.componentId)) {
            change(`${basePath}.category`, destCategory);

            if (roomByRoom) {
                const nextRoomId = isRoomView ? destRoomId : `room-default-${destCategory}`;
                change(`${basePath}.roomId`, nextRoomId);

                if (sourceCategory !== destCategory) {
                    const data = getAtComponentPath(components, item.draggableId);
                    if (data != null) {
                        updateNestedSubcomponentCategories(change, item.draggableId, data, destCategory);
                    }
                }
            }
        }
    });

    if (source.droppableId !== destination.droppableId) {
        const sourceRemaining = allMapped
            .filter(matchesSource)
            .filter(({ componentId }) => !movingIds.has(componentId));

        sourceRemaining.forEach((item, index) => {
            change(
                `model.modelData.components.${item.componentType}.${item.componentId}.displayOrder`,
                index
            );
        });
    }
};

const multiMoveSubcomponents = (change, destination, source, components, toMoveIds) => {
    const destParentId = destination.droppableId;
    if (!destParentId) return;

    const destParentData = getAtComponentPath(components, destParentId) ?? {};
    const destCategory = destParentData.category || "";
    const destSubs = destParentData.subcomponents || {};

    const moves = toMoveIds.map((id) => ({
        oldId: id,
        type: getComponentTypeFromDraggableId(id),
        data: cloneDeep(getAtComponentPath(components, id)),
        sourceParentId: getParentDraggableId(id),
        oldComponentId: id.split(".").pop(),
    }));

    const isMovingDestItem = ({ componentType, componentId }) =>
        moves.some(
            (m) =>
                m.sourceParentId === destParentId &&
                m.type === componentType &&
                m.oldComponentId === componentId
        );

    const destFullSubs = mapComponents(destSubs)
        .filter((el) => !isEmpty(el))
        .sort((a, b) => a.displayOrder - b.displayOrder);

    const destSubsExisting = destFullSubs.filter((item) => !isMovingDestItem(item));

    const movingBeforeDest = destFullSubs.slice(0, destination.index).filter(isMovingDestItem).length;
    let insertAt = destination.index - movingBeforeDest;
    insertAt = Math.max(0, Math.min(insertAt, destSubsExisting.length));

    const placedMoves = moves.map((m) => {
        if (m.sourceParentId !== destParentId) {
            const newId = uniqid(`${m.type}-`);
            return {
                ...m,
                newComponentId: newId,
                newPath: `${destParentId}.subcomponents.${m.type}.${newId}`,
                isCrossParent: true,
            };
        }

        return {
            ...m,
            newComponentId: m.oldComponentId,
            newPath: m.oldId,
            isCrossParent: false,
        };
    });

    const newDestList = [
        ...destSubsExisting.slice(0, insertAt).map((item) => ({
            componentType: item.componentType,
            componentId: item.componentId,
            path: `${destParentId}.subcomponents.${item.componentType}.${item.componentId}`,
        })),
        ...placedMoves.map((m) => ({
            componentType: m.type,
            componentId: m.newComponentId,
            path: m.newPath,
            move: m,
        })),
        ...destSubsExisting.slice(insertAt).map((item) => ({
            componentType: item.componentType,
            componentId: item.componentId,
            path: `${destParentId}.subcomponents.${item.componentType}.${item.componentId}`,
        })),
    ];

    placedMoves.forEach((m) => {
        if (!m.isCrossParent || m.data == null) return;

        const accessor = m.oldId.slice(m.sourceParentId.length);
        change(`model.modelData.components.${m.sourceParentId}${accessor}`, {});

        const targetCompData = {
            ...m.data,
            category: destCategory,
            displayOrder: 0,
        };

        if (m.data.subcomponents) {
            const sourceCategory = getAtComponentPath(components, m.sourceParentId)?.category || "";
            if (destCategory !== sourceCategory) {
                Object.entries(m.data.subcomponents).forEach(([compType, compList]) => {
                    Object.keys(compList || {}).forEach((compId) => {
                        targetCompData.subcomponents[compType][compId] = {
                            ...targetCompData.subcomponents[compType][compId],
                            category: destCategory,
                        };
                    });
                });
            }
        }

        change(`model.modelData.components.${m.newPath}`, targetCompData);
    });

    newDestList.forEach((item, index) => {
        change(`model.modelData.components.${item.path}.displayOrder`, index);
        if (item.move && !item.move.isCrossParent) {
            change(`model.modelData.components.${item.path}.category`, destCategory);
        }
    });

    const affectedSources = [
        ...new Set(placedMoves.filter((m) => m.isCrossParent).map((m) => m.sourceParentId).filter(Boolean)),
    ];

    affectedSources.forEach((srcParentId) => {
        const srcParent = getAtComponentPath(components, srcParentId);
        const remaining = mapComponents(srcParent?.subcomponents || {})
            .filter((el) => !isEmpty(el))
            .filter(
                ({ componentType, componentId }) =>
                    !placedMoves.some(
                        (m) =>
                            m.isCrossParent &&
                            m.sourceParentId === srcParentId &&
                            m.type === componentType &&
                            m.oldComponentId === componentId
                    )
            )
            .sort((a, b) => a.displayOrder - b.displayOrder);

        remaining.forEach((item, index) => {
            change(
                `model.modelData.components.${srcParentId}.subcomponents.${item.componentType}.${item.componentId}.displayOrder`,
                index
            );
        });
    });
};

export const onMultiDragEnd = (
    change,
    destination,
    source,
    activeDraggableId,
    typeOfDrop,
    components,
    selectedIds = [],
    options = {}
) => {
    const { roomByRoom = false, isRoomView = false, intComponents } = options;

    if (!destination || !activeDraggableId) return;

    const isSubComponent = typeOfDrop === "component" || typeOfDrop === "subcomponent";

    if (
        isSubComponent &&
        intComponents &&
        destination.droppableId &&
        getDraggablePosition(activeDraggableId, intComponents) !==
            getDraggablePosition(destination.droppableId, intComponents)
    ) {
        return;
    }

    let toMoveIds = getCompatibleSelectedIds(activeDraggableId, selectedIds, intComponents);
    toMoveIds = filterAllowedAtDestination(toMoveIds, destination, typeOfDrop);

    if (!toMoveIds.includes(activeDraggableId)) {
        toMoveIds = [activeDraggableId];
    }

    toMoveIds = sortDraggableIdsByDisplayOrder(toMoveIds, components);

    if (toMoveIds.length <= 1) {
        if (roomByRoom) {
            return onDragEndRoomByRoom(
                change,
                destination,
                source,
                activeDraggableId,
                typeOfDrop,
                components,
                isRoomView
            );
        }
        return onDragEnd(change, destination, source, activeDraggableId, typeOfDrop, components);
    }

    if (isSubComponent) {
        return multiMoveSubcomponents(change, destination, source, components, toMoveIds);
    }

    return multiMoveRoots(change, destination, source, components, toMoveIds, {
        isRoomView,
        roomByRoom,
    });
};

export const mapComponents = (components, destination) =>
    Object.entries(components)
        .reduce((prev, [componentType, components]) => {
            if (components == null) {
                return prev;
            } else {
                const mappedComponents = Object.entries(components).map(([key, val]) => {
                    if (val === null || val === undefined || isEmpty(val)) {
                        return {};
                    } else {
                        const { displayOrder, category, label = "", roomId } = val;
                        return {
                            componentId: key,
                            componentType,
                            displayOrder,
                            category,
                            label,
                            roomId,
                        };
                    }
                });

                return [...prev, ...mappedComponents];
            }
        }, [])
        .sort((a, b) => {
            if (a.displayOrder !== b.displayOrder) {
                return a.displayOrder - b.displayOrder;
            }

            if (a.label !== b.label) {
                if (a.label.toLowerCase() < b.label.toLowerCase()) {
                    return -1;
                }
                if (a.label.toLowerCase() > b.label.toLowerCase()) {
                    return 1;
                }
                return 0;
            }

            return 0;
        });

export const checkMousePosition = (event, currentPosition) => {
    const rect = event.currentTarget.getBoundingClientRect();

    const y = event.clientY - rect.top;

    if (y < 0) return "top";

    const halfOfDiv = rect.height / 2;

    return y < halfOfDiv && y > 0 ? "top" : "bottom";
};

export const dragStart = (index, id, draggableId, droppableId, setSource, setDraggableId) => {
    setSource({ index, droppableId, id });
    setDraggableId(draggableId);
};

export const onDragEnd = (change, destination, source, draggableId, typeOfDrop, components) => {
    const isSubComponent = typeOfDrop === "component" || typeOfDrop === "subcomponent";
    let movingComponentId = draggableId.split(".")[draggableId.split(".").length - 1];

    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
        return;
    }

    if (isSubComponent) {
        const { droppableId: destinationId, index: destinationIndex } = destination;
        const { droppableId: sourceId, index: sourceIndex } = source;

        if (!destinationId) return;

        const accessor = draggableId.split(sourceId)[1];

        const draggableComponentData = getAtComponentPath(components, draggableId);
        if (draggableComponentData == null) return;

        const { category: destinationCategory = "", subcomponents = {} } =
            getAtComponentPath(components, destinationId) ?? {};

        const targetCompData = {
            ...draggableComponentData,
            category: destinationCategory,
            displayOrder: destinationIndex,
        };

        if (destinationId !== sourceId) {
            // Remove from previous parent component
            change(`model.modelData.components.${sourceId}${accessor}`, {});

            // Add to new parent component
            const newAccessor = accessor.replace(
                `${accessor.split(".")[accessor.split(".").length - 1]}`,
                `${uniqid(`${accessor.split(".")[2]}-`)}`
            );

            movingComponentId = newAccessor.split(".")[newAccessor.split(".").length - 1];

            change(`model.modelData.components.${destinationId}${newAccessor}`, targetCompData);
        } else {
            change(`model.modelData.components.${draggableId}.displayOrder`, destinationIndex);
        }

        return mapComponents(subcomponents)
            .filter((el) => !isEmpty(el))
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(({ componentType: type, componentId: id }, currentIndex) => {
                if (id === movingComponentId) {
                    return draggableId;
                }
                if (destinationId === sourceId && sourceIndex < currentIndex && destinationIndex >= currentIndex) {
                    change(
                        `model.modelData.components.${destinationId}.subcomponents.${type}.${id}.displayOrder`,
                        currentIndex - 1
                    );
                    return draggableId;
                }

                if (sourceIndex > currentIndex && destinationIndex <= currentIndex) {
                    change(
                        `model.modelData.components.${destinationId}.subcomponents.${type}.${id}.displayOrder`,
                        currentIndex + 1
                    );
                    return draggableId;
                }

                change(
                    `model.modelData.components.${destinationId}.subcomponents.${type}.${id}.displayOrder`,
                    currentIndex
                );
            });
    }

    if (!isSubComponent && destination.droppableId !== source.droppableId) {
        change(`model.modelData.components.${draggableId}.category`, destination.droppableId);
    }

    if (!isSubComponent && destination.index !== source.index) {
        let destinationIndex = destination.index;

        //  Update moved component with new display order
        change(`model.modelData.components.${draggableId}.displayOrder`, destinationIndex);

        //  Get sorted components and filter with desination category, then shift other components up or down
        mapComponents(components, destination)
            .filter(({ category }) => category === destination.droppableId)
            .map(({ componentType: type, componentId: id }, currentIndex) => {
                if (id === movingComponentId) {
                    return draggableId;
                }

                if (
                    destination.droppableId === source.droppableId &&
                    source.index < currentIndex &&
                    destinationIndex >= currentIndex
                ) {
                    change(`model.modelData.components.${type}.${id}.displayOrder`, currentIndex - 1);
                    return draggableId;
                }

                if (source.index > currentIndex && destinationIndex <= currentIndex) {
                    change(`model.modelData.components.${type}.${id}.displayOrder`, currentIndex + 1);
                    return draggableId;
                }

                change(`model.modelData.components.${type}.${id}.displayOrder`, currentIndex);
            });
    }
}; // Note: Doesn't update subcomponents' category when parent is moved

export const onDragEndRoomByRoom = (change, destination, source, draggableId, typeOfDrop, components, isRoomView) => {
    const isSubComponent = typeOfDrop === "component" || typeOfDrop === "subcomponent";
    const isComponent = typeOfDrop === "room";
    const isRoom = typeOfDrop === "storey"; // Not yet implemented

    let movingComponentId = draggableId.split(".")[draggableId.split(".").length - 1];

    // If no destination OR destination id is the source Id and have the same index, do nothing
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
        return;
    }

    // Get component id and index (display order
    const { droppableId: destinationId, index: destinationIndex } = destination;
    const { droppableId: sourceId, index: sourceIndex } = source;

    // Moving component to another component
    if (isSubComponent) {
        // If no destination, do nothing
        if (!destinationId) return;

        // Get accessor for dragged subcomponent by removing old parent component's id (sourceID)
        // basement.basement-0 (REMOVED) .subcomponents.door.door-0 (GRABBED)
        const accessor = draggableId.split(sourceId)[1];

        const draggableComponentData = getAtComponentPath(components, draggableId);
        if (draggableComponentData == null) return;

        const { category: destinationCategory = "", subcomponents = {} } =
            getAtComponentPath(components, destinationId) ?? {};

        // Update subcomponents' category if category is different
        if (draggableComponentData.subcomponents) {
            const { category: sourceCategory = "" } = getAtComponentPath(components, sourceId) ?? {};

            if (destinationCategory != sourceCategory) {
                Object.entries(draggableComponentData.subcomponents).forEach(([compType, compList]) => {
                    Object.keys(compList).forEach((compId) => {
                        const subcomponentPath = `model.modelData.components.${draggableId}.subcomponents.${compType}.${compId}.category`;
                        change(subcomponentPath, destinationCategory);
                    });
                });
            }
        }

        const targetCompData = {
            ...draggableComponentData,
            category: destinationCategory,
            displayOrder: destinationIndex,
        };

        // Moved to another parent component
        if (destinationId !== sourceId) {
            // Remove from previous parent component
            change(`model.modelData.components.${sourceId}${accessor}`, {});

            // Replaces old id with new id to create new subcomponent accessor (e.g. .subcomponents.door.door-0)
            const newAccessor = accessor.replace(
                `${accessor.split(".")[accessor.split(".").length - 1]}`,
                `${uniqid(`${accessor.split(".")[2]}-`)}`
            );
            // Gets new id (e.g. door-0)
            movingComponentId = newAccessor.split(".")[newAccessor.split(".").length - 1];

            // Add to new parent component with updated category, display order, and same draggableComponentData
            change(`model.modelData.components.${destinationId}${newAccessor}`, targetCompData);
        } else {
            // If component changed places in same parent, update display order
            change(`model.modelData.components.${draggableId}.displayOrder`, destinationIndex);
        }

        //  Get sorted components and filter with desination category, then shift other components up or down
        return mapComponents(subcomponents)
            .filter((el) => !isEmpty(el))
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(({ componentType: type, componentId: id }, currentIndex) => {
                if (id === movingComponentId) {
                    return draggableId;
                }
                if (destinationId === sourceId && sourceIndex < currentIndex && destinationIndex >= currentIndex) {
                    change(
                        `model.modelData.components.${destinationId}.subcomponents.${type}.${id}.displayOrder`,
                        currentIndex - 1
                    );
                    return draggableId;
                }

                if (sourceIndex > currentIndex && destinationIndex <= currentIndex) {
                    change(
                        `model.modelData.components.${destinationId}.subcomponents.${type}.${id}.displayOrder`,
                        currentIndex + 1
                    );
                    return draggableId;
                }

                change(
                    `model.modelData.components.${destinationId}.subcomponents.${type}.${id}.displayOrder`,
                    currentIndex
                );
            });
    }

    const [destStoreyId, destRoomId] = destinationId.split(".");
    const [srcStoreyId, srcRoomId] = sourceId.split(".");

    // Moving a component between rooms
    if (isComponent && isRoomView) {
        // Moved to a different room
        if (destinationId !== sourceId) {
            // Update roomId
            change(`model.modelData.components.${draggableId}.roomId`, destRoomId);

            // Moved to a room in a different category
            if (destStoreyId !== srcStoreyId) {
                // Update storeyId
                change(`model.modelData.components.${draggableId}.category`, destStoreyId);

                // Update subcomponents' category
                const draggableComponentData = getAtComponentPath(components, draggableId);
                if (draggableComponentData != null) {
                    const subComponents = draggableComponentData.subcomponents || {};
                    var updatedSubComponents = cloneDeep(subComponents);

                    if (!!subComponents) {
                        updatedSubComponents = Object.entries(subComponents).reduce((acc, [compType, compList]) => {
                            const newCompList = Object.entries(compList).reduce(
                                (acc, [compId, { subcomponents, ...compData }]) => {
                                    const extraProps = {};

                                    if (!!subcomponents) {
                                        extraProps.subcomponents = Object.fromEntries(
                                            Object.entries(subcomponents).reduce((acc, [subCompType, subCompList]) => {
                                                const newSubCompList = Object.entries(subCompList).reduce(
                                                    (acc, [subCompId, subCompData]) => {
                                                        return [
                                                            ...acc,
                                                            [subCompId, { ...subCompData, category: destStoreyId }],
                                                        ];
                                                    },
                                                    []
                                                );

                                                return [...acc, [subCompType, Object.fromEntries(newSubCompList)]];
                                            }, [])
                                        );
                                    }

                                    return [
                                        ...acc,
                                        [
                                            compId,
                                            {
                                                ...compData,
                                                category: destStoreyId,
                                                ...extraProps,
                                            },
                                        ],
                                    ];
                                },
                                []
                            );

                            return [...acc, [compType, Object.fromEntries(newCompList)]];
                        }, []);
                    }

                    change(
                        `model.modelData.components.${draggableId}.subcomponents`,
                        Object.fromEntries(updatedSubComponents)
                    );
                }
            }
        }

        // Moved to a different index position
        if (destination.index !== source.index) {
            let destinationIndex = destination.index;

            // Update display order of dragged component
            change(`model.modelData.components.${draggableId}.displayOrder`, destinationIndex);

            // Update all components at destination category
            mapComponents(components, destination)
                //  Get only components in desination category and room
                .filter(
                    ({ category, roomId }) =>
                        category === destStoreyId &&
                        (roomId === destRoomId || (!roomId && destRoomId === `room-default-${destStoreyId}`))
                )
                // Get component type and id and index for display order
                .map(({ componentType: type, componentId: id }, currentIndex) => {
                    // If current component id is the component being moved, do nothing, display already set
                    if (id === movingComponentId) {
                        return draggableId;
                    }

                    // If current position in list is larger than source and smaller than destination
                    // (A component was moved below and a space opened up above
                    // Shift current component higher to fill space created above
                    if (
                        destination.droppableId === source.droppableId &&
                        source.index < currentIndex &&
                        destinationIndex >= currentIndex
                    ) {
                        change(`model.modelData.components.${type}.${id}.displayOrder`, currentIndex - 1);
                        return draggableId;
                    }

                    // If current position in list is smaller than source and larger than destination
                    // (A component was moved upward and a space opened up below)
                    // Shift current component lower to fill space created below
                    if (source.index > currentIndex && destinationIndex <= currentIndex) {
                        change(`model.modelData.components.${type}.${id}.displayOrder`, currentIndex + 1);
                        return draggableId;
                    }

                    // If current position is not affected by the move, keep the same index
                    change(`model.modelData.components.${type}.${id}.displayOrder`, currentIndex);
                });
            // Returns list of draggableIds (not used)
        }
    }

    if (isComponent && !isRoomView) {
        // Moved to a different category in StoreyView
        if (destStoreyId !== srcStoreyId) {
            // Update roomId
            change(`model.modelData.components.${draggableId}.roomId`, `room-default-${destStoreyId}`);

            // Update storeyId
            change(`model.modelData.components.${draggableId}.category`, destStoreyId);

            // Update subcomponents' category
            const draggableComponentData = getAtComponentPath(components, draggableId);
            if (draggableComponentData != null) {
                const subComponents = draggableComponentData.subcomponents || {};
                var updatedSubComponents = cloneDeep(subComponents);

                if (!!subComponents) {
                    updatedSubComponents = Object.entries(subComponents).reduce((acc, [compType, compList]) => {
                        const newCompList = Object.entries(compList).reduce(
                            (acc, [compId, { subcomponents, ...compData }]) => {
                                const extraProps = {};

                                if (!!subcomponents) {
                                    extraProps.subcomponents = Object.fromEntries(
                                        Object.entries(subcomponents).reduce((acc, [subCompType, subCompList]) => {
                                            const newSubCompList = Object.entries(subCompList).reduce(
                                                (acc, [subCompId, subCompData]) => {
                                                    return [
                                                        ...acc,
                                                        [subCompId, { ...subCompData, category: destStoreyId }],
                                                    ];
                                                },
                                                []
                                            );

                                            return [...acc, [subCompType, Object.fromEntries(newSubCompList)]];
                                        }, [])
                                    );
                                }

                                return [
                                    ...acc,
                                    [
                                        compId,
                                        {
                                            ...compData,
                                            category: destStoreyId,
                                            ...extraProps,
                                        },
                                    ],
                                ];
                            },
                            []
                        );

                        return [...acc, [compType, Object.fromEntries(newCompList)]];
                    }, []);
                }

                change(`model.modelData.components.${draggableId}.subcomponents`, Object.fromEntries(updatedSubComponents));
            }
        }

        // Moved to a different index position (?)
        if (destination.index !== source.index) {
            return;
        }
    }

    // Moving a room between storeys
    if (isRoom) {
        return;
    }
};
